import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { VALIDATION } from "../../../../constants/ship";
import {
  setShipSize,
  setTechLevel,
  setHull,
  setHullConfiguration,
  setBridge,
} from "../../../../store/features/shipBuilderSlice";
import { HULL_CONFIGURATIONS } from "../../../../constants/hull";
import { BRIDGE_TYPES, calculateStandardBridgeSize, calculateStandardBridgeCost } from "../../../../constants/bridges";
import { SaveLoadButtons } from "../../../SaveLoadButtons";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';


/**
 * Component for selecting ship size and technology level
 * Uses Redux for state management
 *
 * @component
 * @example
 * ```tsx
 * <ShipSizeSelector />
 * ```
 */
export const ShipSizeSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipSize = useAppSelector(selectors.selectShipSize);
  const techLevel = useAppSelector(selectors.selectTechLevel);
  const bridge = useAppSelector((state) => state.shipBuilder.bridge);
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);

  // Local state for tech level input to allow typing multi-digit numbers
  const [techLevelInput, setTechLevelInput] = React.useState<string>((techLevel || 8).toString());
  
  // Local state for ship size input to allow typing multi-digit numbers
  const [shipSizeInput, setShipSizeInput] = React.useState<string>((shipSize || 100).toString());

  // Initialize Redux state with default values if they're undefined
  React.useEffect(() => {
    if (!shipSize || shipSize <= 0) {
      dispatch(setShipSize(100));
    }
    if (!techLevel || techLevel < 7) {
      dispatch(setTechLevel(8));
    }
  }, [shipSize, techLevel, dispatch]);

  // Check if ship has jump drives
  const hasJumpDrive = drives && drives.some(drive => drive.type === "jump" && drive.rating > 0);

  /**
   * Auto-select Standard Bridge when ship size is defined
   */
  const setStandardBridge = React.useCallback((size: number) => {
    // Only set bridge if none is currently selected, or update existing Standard bridge
    if (!bridge || bridge.type === "STANDARD") {
      const bridgeSize = calculateStandardBridgeSize(size);
      const bridgeCost = calculateStandardBridgeCost(size);
      const standardBridge = BRIDGE_TYPES.STANDARD;
      
      const newBridge = {
        ...standardBridge,
        cost: bridgeCost,
        weight: bridgeSize,
        maintenanceCost: bridgeCost * 0.01, // 1% of cost per month
      };
      
      dispatch(setBridge(newBridge));
    }
  }, [bridge, dispatch]);

  // Auto-select Standard Bridge when ship size and tech level are both defined and no bridge is selected
  React.useEffect(() => {
    if (shipSize >= VALIDATION.minShipSize && techLevel >= VALIDATION.minTechLevel && !bridge) {
      setStandardBridge(shipSize);
    }
  }, [shipSize, techLevel, bridge, setStandardBridge]);

  // Sync local ship size input state with Redux state
  React.useEffect(() => {
    setShipSizeInput((shipSize || 100).toString());
  }, [shipSize]);

  /**
   * Get the appropriate increment step based on current ship size
   * Uses "rounding" behavior as requested
   */
  const getIncrementStep = (currentSize: number): number => {
    if (currentSize < 100) return 10;      // 10-ton increments for small ships
    if (currentSize < 1000) return 100;    // 100-ton increments for medium ships
    if (currentSize < 10000) return 500;   // 500-ton increments for large ships
    if (currentSize < 100000) return 1000; // 1000-ton increments for huge ships
    return 10000;                          // 10000-ton increments for massive ships
  };

  /**
   * Round a value to the nearest increment step
   */
  const roundToIncrement = (value: number, step: number): number => {
    return Math.round(value / step) * step;
  };

  /**
   * Handle increment button click
   */
  const handleIncrement = () => {
    const step = getIncrementStep(shipSize);
    let newSize = shipSize + step;
    
    // Ensure we don't go above maximum
    if (newSize > VALIDATION.maxShipSize) {
      newSize = VALIDATION.maxShipSize;
    }
    
    // Round to the appropriate step for the new size
    const newStep = getIncrementStep(newSize);
    newSize = roundToIncrement(newSize, newStep);
    
      if (newSize <= VALIDATION.maxShipSize) {
        dispatch(setShipSize(newSize));
        updateHullConfiguration(newSize);
        setStandardBridge(newSize);
      }
  };

  /**
   * Handle decrement button click
   */
  const handleDecrement = () => {
    const step = getIncrementStep(shipSize);
    let newSize = shipSize - step;
    
    // If the result would be in a different step size range, 
    // use the step size that would naturally get us to the previous "round number"
    if (newSize < 100 && shipSize >= 100) {
      // Transitioning from 100+ range to <100 range, use 10-ton steps
      newSize = Math.max(90, VALIDATION.minShipSize);
    } else if (newSize < 1000 && shipSize >= 1000) {
      // Transitioning from 1000+ range to <1000 range, use 100-ton steps
      newSize = Math.max(900, 100);
    } else if (newSize < 10000 && shipSize >= 10000) {
      // Transitioning from 10000+ range to <10000 range, use 500-ton steps
      newSize = Math.max(9500, 1000);
    } else if (newSize < 100000 && shipSize >= 100000) {
      // Transitioning from 100000+ range to <100000 range, use 1000-ton steps
      newSize = Math.max(99000, 10000);
    } else {
      // Normal case - round to the appropriate step for the new size
      const newStep = getIncrementStep(newSize);
      newSize = roundToIncrement(newSize, newStep);
    }
    
    // Ensure we don't go below minimum
    if (newSize < VALIDATION.minShipSize) {
      newSize = VALIDATION.minShipSize;
    }
    
      if (newSize >= VALIDATION.minShipSize) {
        dispatch(setShipSize(newSize));
        updateHullConfiguration(newSize);
        setStandardBridge(newSize);
      }
  };

  /**
   * Update hull configuration when ship size changes
   */
  const updateHullConfiguration = (size: number) => {
    const defaultConfig = HULL_CONFIGURATIONS.find(
      (c) => c.type === "standard",
    );
    if (defaultConfig) {
      dispatch(
        setHull({
          size: size,
          type: defaultConfig.type,
          configuration: defaultConfig,
          cost: size * defaultConfig.costPerTon,
          weight: size,
          hullPoints: 0, // Will be calculated later
        }),
      );
    }
  };

  /**
   * Handles ship size change from manual input
   * Allow typing any numeric input, validate on blur
   * @param event - The change event from the input
   */
  const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    
    // Allow empty input or any numeric input while typing
    if (inputValue === '' || /^\d*$/.test(inputValue)) {
      setShipSizeInput(inputValue);
    }
  };

  /**
   * Handles ship size input blur - validate and update Redux state
   * @param event - The blur event from the input
   */
  const handleSizeBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const size = parseInt(inputValue);
    
    if (!isNaN(size)) {
      // Clamp to valid range
      const clampedSize = Math.max(VALIDATION.minShipSize, Math.min(size, VALIDATION.maxShipSize));
      
      // Update local state with clamped value
      setShipSizeInput(clampedSize.toString());
      
      // Update Redux state
      dispatch(setShipSize(clampedSize));
      updateHullConfiguration(clampedSize);
      setStandardBridge(clampedSize);
    } else {
      // Invalid input, reset to current ship size
      setShipSizeInput((shipSize || 100).toString());
    }
  };

  /**
   * Handles tech level change and updates all relevant selections
   * @param event - The change event from the input
   */
  const handleTechLevelChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = event.target.value;
    
    // Allow any input while typing (including partial numbers)
    setTechLevelInput(inputValue);
  };

  const handleTechLevelBlur = () => {
    const level = parseInt(techLevelInput);
    
    // Validate and update Redux state on blur
    if (
      !isNaN(level) &&
      level >= VALIDATION.minTechLevel &&
      level <= VALIDATION.maxTechLevel
    ) {
      dispatch(setTechLevel(level));
      
      // Armor and power plant selection are handled by their respective components
      
      // Auto-select Standard Bridge if ship size is also valid
      if (shipSize >= VALIDATION.minShipSize) {
        setStandardBridge(shipSize);
      }
    } else {
      // Reset to current valid value if input is invalid
      setTechLevelInput((techLevel || 8).toString());
    }
  };

  // Update local input when Redux state changes (e.g., from other components)
  React.useEffect(() => {
    setTechLevelInput((techLevel || 8).toString());
  }, [techLevel]);



  return (
    <div className="ship-size-selector">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-highlightOrange">
          Ship Size and Technology Level
        </h2>
        <div className="flex gap-2">
          <SaveLoadButtons variant="compact" />
        </div>
      </div>



      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Ship Size (tons)
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            {/* Decrement Button */}
            <button
              type="button"
              onClick={handleDecrement}
              disabled={shipSize <= VALIDATION.minShipSize}
              className="relative -mr-px inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 focus:z-10 focus:border-highlightOrange focus:outline-none focus:ring-1 focus:ring-highlightOrange disabled:opacity-50 disabled:cursor-not-allowed rounded-l-md"
              title={`Decrease by ${getIncrementStep(shipSize)} tons`}
            >
              <RemoveIcon sx={{ fontSize: 16 }} />
            </button>
            
            {/* Input Field */}
            <input
              type="number"
              min={VALIDATION.minShipSize}
              max={VALIDATION.maxShipSize}
              value={shipSizeInput}
              onChange={handleSizeChange}
              onBlur={handleSizeBlur}
              className="relative block w-full rounded-none border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:z-10 focus:border-highlightOrange focus:outline-none focus:ring-1 focus:ring-highlightOrange sm:text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="Enter ship size"
            />
            
            {/* Increment Button */}
            <button
              type="button"
              onClick={handleIncrement}
              disabled={shipSize >= VALIDATION.maxShipSize}
              className="relative -ml-px inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 focus:z-10 focus:border-highlightOrange focus:outline-none focus:ring-1 focus:ring-highlightOrange disabled:opacity-50 disabled:cursor-not-allowed rounded-r-md"
              title={`Increase by ${getIncrementStep(shipSize)} tons`}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Enter the desired size of your ship in tons. Use buttons for {getIncrementStep(shipSize)}-ton increments, or type any value manually. Minimum size is{" "}
            {VALIDATION.minShipSize} tons (Traveller rules: jump-capable ships require minimum 100 tons).
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Technology Level
          </label>
          <input
            type="number"
            min={VALIDATION.minTechLevel}
            max={VALIDATION.maxTechLevel}
            value={techLevelInput}
            onChange={handleTechLevelChange}
            onBlur={handleTechLevelBlur}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-highlightOrange focus:ring-highlightOrange sm:text-sm dark:bg-gray-700 dark:text-gray-100"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Select the technology level of your ship. Level must be between{" "}
            {VALIDATION.minTechLevel} and {VALIDATION.maxTechLevel}.
          </p>
        </div>
      </div>


      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded">
        <h3 className="text-lg font-bold text-paleBlue dark:text-blue-300 mb-2">
          Current Configuration
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-300">Ship Size:</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{shipSize} tons</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-300">Technology Level:</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">TL {techLevel}</p>
          </div>
        </div>
      </div>

      {/* Hull Size Validation Warnings */}
      {shipSize < VALIDATION.minShipSize && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Invalid Hull Size
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>
                  <strong>Traveller Rules:</strong> A hull must be a minimum of {VALIDATION.minShipSize} tons. 
                  Your current ship size of {shipSize} tons violates this rule.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasJumpDrive && shipSize < VALIDATION.minJumpCapableShipSize && (
        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Jump-Capable Ship Size Warning
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                <p>
                  <strong>Traveller Rules:</strong> Jump-capable ships must be at least {VALIDATION.minJumpCapableShipSize} tons. 
                  Your current ship size of {shipSize} tons is too small for a jump drive. 
                  Either increase the ship size to {VALIDATION.minJumpCapableShipSize}+ tons or remove the jump drive.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
